import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Tabs, Tab } from 'react-bootstrap';
import { Card, Pagination, Button, message, Spin, Image, Modal as AntModal, Input, Upload, Tooltip } from 'antd';
import { EyeOutlined, DeleteOutlined, ExclamationCircleOutlined, RightOutlined, LeftOutlined, EditOutlined, SaveOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import LoadingWheel from '../loadingwheel';
import sensorboxGroupService from '../../services/sensorboxGroupService';
import issueTrackerServices from '../../services/issueTrackerServices';
import userService from '../../services/userService';
import groupServices from '../../services/groupServices';
import assetMaintenanceNotesService from '../../services/assetMaintenanceNotesService';
import userStore from '../../store/userStore';
import toast from '../toast';
import moment from 'moment';
import authStore from '../../store/authStore';
import Select from '../select';
import DatePicker from '../datepicker';
import noAttachmentYet from '../../assets/images/no_attachment_yet.png';
import uploadImageLogo from '../../assets/images/image-upload.png';
import PlusBtn from '../../assets/add.png';
import NoSensorBoxSelected from '../../assets/sensor_empty.png';
import Close from '../../assets/close.png';
import Routes from '../../routing/routes';
import Permissions from '../../config/permssions';
import constants from '../../config/constants';
import './assetDetailsModal.css';
import '../creategroupmodal/attachment-upload-modal.css';
import AlertRecipientsCard from '../AlertRecipientsCard';
import { mergeTeamRecipients, splitTeamRecipients, AlertRecipient } from '../../utils/alertRecipients';
import DocumentsPanel from '../documentsPanel';
import sensorboxGroupDocumentationService from '../../services/sensorboxGroupDocumentationService';
import AssetDocumentsRollup from './AssetDocumentsRollup';

const {
  view_contractor_sensorboxes_assets,
  view_assigned_users_sensorboxes_assets,
  create_or_update_sensorboxes_assets,
  view_only_partial_asset_sensorbox_details,
  view_all_issue_tracker
} = Permissions;

interface Props {
  show: boolean;
  onClose: () => void;
  asset: any;
  organisationId?: string;
  onSave?: () => void;
}

const AssetDetailsModal: React.FC<Props> = ({ show, onClose, asset, organisationId, onSave }) => {
  const history = useHistory();
  const canViewIssues = authStore?.currentUser?.access?.includes(view_all_issue_tracker);
  const [loading, setLoading] = useState(false);
  const [assetDetails, setAssetDetails] = useState<any>(null);
  const [issues, setIssues] = useState<Array<any>>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('maintenance');
  const [isEdit, setIsEdit] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [locDetails, setLocDetails] = useState('');
  const [mergeTimeout, setMergeTimeout] = useState('');
  const [formData, setFormData] = useState<any>({});
  const [consumerList, setConsumerList] = useState<Array<any>>([]);
  const [contractorList, setContractorList] = useState<Array<any>>([]);
  const [userAccess, setUserAccess] = useState<any>([]);
  const [externalLinks, setExternalLinks] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [processing, setProcessing] = useState(false);
  
  // Sensorboxes state
  const [sensorboxList, setSensorboxList] = useState<Array<any>>([]);
  const [sensorboxes, setSensorboxes] = useState<Array<any>>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // Maintenance Notes state
  const [maintenanceNotes, setMaintenanceNotes] = useState<Array<any>>([]);
  const [maintenanceNotesLoading, setMaintenanceNotesLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [newNote, setNewNote] = useState({ notes: '', status: 'OPEN', created_on: new Date() as Date | null });
  const [editingNote, setEditingNote] = useState({ notes: '', status: 'OPEN', created_on: null as Date | null });
  
  // Attachments state
  const [attachments, setAttachments] = useState<any[]>([]);
  const [selectedAttachmentType, setSelectedAttachmentType] = useState('model');
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [hoveredFile, setHoveredFile] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<{[key: string]: string}>({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);

  useEffect(() => {
    if (show && asset?._id) {
      fetchAssetDetails();
      getCustomerList();
      getContractorList();
    } else if (!show) {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, asset?._id]);

  useEffect(() => {
    if (assetDetails && show) {
      fetchSensorboxes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetDetails?._id, show]);

  useEffect(() => {
    if (show && assetDetails && activeTab === 'issues' && issues.length === 0 && !issuesLoading) {
      fetchIssues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, assetDetails?._id]);

  useEffect(() => {
    if (show && assetDetails && activeTab === 'maintenance' && maintenanceNotes.length === 0 && !maintenanceNotesLoading) {
      fetchMaintenanceNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, assetDetails?._id]);

  const clear = () => {
    setName('');
    setLocDetails('');
    setMergeTimeout('');
    setFormData({});
    setUserAccess([]);
    setErrors({});
    setExternalLinks([]);
    setAttachments([]);
    setSelectedAttachmentType('model');
    setIsEdit(false);
    setCurrentAttachmentIndex(0);
    setCurrentFileIndex(0);
    setHoveredFile(null);
    setImageUrls({});
    setPreviewVisible(false);
    setShowAttachmentModal(false);
    setSensorboxes([]);
    setSearchKeyword('');
  };

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      const response = await sensorboxGroupService.getSensorboxGroupById(asset._id, organisationId);
      if (response?.status === 200 && response?.data) {
        const data = response.data;
        setAssetDetails(data);
        setName(data.name || '');
        setLocDetails(data.loc_details || '');
        setMergeTimeout(data.group_merge_timeout?.toString() || '');
        setFormData(data);
        setExternalLinks(data.external_reference_links || []);
        setAttachments(data.attachments || []);
        setSensorboxes(data.sensorboxes || []);
        
        // Set user access
        if (data.user_access && data.user_access.length > 0 && consumerList.length > 0) {
          const userAccessArray = consumerList?.filter((item: any) =>
            data.user_access?.includes(item?._id)
          );
          const filteredUserAccess = userAccessArray?.map((item: any) => ({ value: item._id, label: item.name }));
          setUserAccess(filteredUserAccess || []);
        }
        
        // Fetch image URLs for attachments
        if (data.attachments && data.attachments.length > 0) {
          await fetchImageUrls(data.attachments);
        }
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      toast.error(error?.response?.data?.message || 'Failed to fetch asset details');
    }
  };

  const fetchSensorboxes = async () => {
    try {
      const resp = await groupServices.getSensorboxesForGroup(organisationId);
      if (resp) {
        let data = resp.data;
        if (assetDetails?.sensorboxes && assetDetails.sensorboxes.length > 0) {
          // Merge existing sensorboxes with available ones, avoiding duplicates
          const existingIds = assetDetails.sensorboxes.map((sb: any) => sb._id);
          const uniqueNew = data.filter((sb: any) => !existingIds.includes(sb._id));
          data = [...assetDetails.sensorboxes, ...uniqueNew];
        }
        setSensorboxList(data);
      }
    } catch (error: any) {
      let errMsg = error?.message;
      if (error?.response?.data?.message) {
        errMsg = error?.response?.data?.message;
      }
      toast.error(errMsg);
    }
  };

  const getCustomerList = async () => {
    try {
      let orgid = organisationId || asset?.org_id || '';
      let consumerListData = await userService.getCustomerList(orgid);
      setConsumerList(consumerListData);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch customer list');
    }
  };

  const getContractorList = async () => {
    try {
      let contractorListData = [];
      let selectedItem;
      if (authStore?.currentUser?.access?.includes(view_contractor_sensorboxes_assets)) {
        selectedItem = { _id: authStore.currentUser.uid, name: authStore.currentUser.name };
        contractorListData = [selectedItem];
      } else if (authStore?.currentUser?.access?.includes(view_assigned_users_sensorboxes_assets)) {
        selectedItem = userStore.profileDetials && userStore.profileDetials.assigned_to;
        contractorListData = [selectedItem];
      } else {
        let orgid = organisationId || asset?.org_id || '';
        contractorListData = await userService.getContractorList(orgid);
      }
      setContractorList(contractorListData);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch contractor list');
    }
  };

  const fetchIssues = async () => {
    try {
      setIssuesLoading(true);
      const sensorboxIds = assetDetails?.sensorboxes?.map((sb: any) => sb._id) || [];
      
      // Add asset _id to the array to fetch asset-level issues
      if (assetDetails?._id) {
        sensorboxIds.push(assetDetails._id);
      }
      
      if (sensorboxIds.length === 0) {
        setIssues([]);
        setIssuesLoading(false);
        return;
      }
      
      const result = await issueTrackerServices.getAllIssuesBySensorboxIds(sensorboxIds, organisationId);
      setIssues(result.issues || []);
      setIssuesLoading(false);
    } catch (error: any) {
      setIssuesLoading(false);
      toast.error(error?.response?.data?.message || 'Failed to fetch issues');
    }
  };

  const fetchMaintenanceNotes = async () => {
    try {
      setMaintenanceNotesLoading(true);
      if (!assetDetails?._id) {
        setMaintenanceNotes([]);
        setMaintenanceNotesLoading(false);
        return;
      }
      
      const result = await assetMaintenanceNotesService.getMaintenanceNotes(assetDetails._id);
      setMaintenanceNotes(result.data || []);
      setMaintenanceNotesLoading(false);
    } catch (error: any) {
      setMaintenanceNotesLoading(false);
      toast.error(error?.response?.data?.message || 'Failed to fetch maintenance notes');
    }
  };

  const handleCreateMaintenanceNote = async () => {
    try {
      if (!newNote.notes.trim()) {
        toast.error('Notes cannot be empty');
        return;
      }
      
      // Convert date to timestamp (milliseconds)
      const created_on = newNote.created_on 
        ? newNote.created_on.getTime() 
        : Date.now();
      
      await assetMaintenanceNotesService.createMaintenanceNote({
        asset_id: assetDetails._id,
        notes: newNote.notes,
        status: newNote.status,
        created_on: created_on
      });
      
      setNewNote({ notes: '', status: 'OPEN', created_on: new Date() });
      await fetchMaintenanceNotes();
      toast.success('Maintenance note created successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to create maintenance note');
    }
  };

  const handleUpdateMaintenanceNote = async (noteId: string) => {
    try {
      if (!editingNote.notes.trim()) {
        toast.error('Notes cannot be empty');
        return;
      }
      
      // Convert date to timestamp (milliseconds)
      const created_on = editingNote.created_on 
        ? editingNote.created_on.getTime() 
        : Date.now();
      
      await assetMaintenanceNotesService.updateMaintenanceNote(noteId, {
        note_id: noteId,
        notes: editingNote.notes,
        status: editingNote.status,
        created_on: created_on
      });
      
      setEditingNoteId(null);
      setEditingNote({ notes: '', status: 'OPEN', created_on: null });
      await fetchMaintenanceNotes();
      toast.success('Maintenance note updated successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update maintenance note');
    }
  };

  const handleDeleteMaintenanceNote = async (noteId: string) => {
    try {
      AntModal.confirm({
        title: 'Delete Maintenance Note',
        icon: <ExclamationCircleOutlined />,
        content: 'Are you sure you want to delete this maintenance note?',
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: async () => {
          try {
            await assetMaintenanceNotesService.deleteMaintenanceNote(noteId);
            await fetchMaintenanceNotes();
            toast.success('Maintenance note deleted successfully');
          } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Failed to delete maintenance note');
          }
        }
      });
    } catch (error: any) {
      toast.error('Failed to delete maintenance note');
    }
  };

  const handleStartEdit = (note: any) => {
    setEditingNoteId(note._id);
    // Set created_on to existing date or current date
    const createdOnDate = note.created_on 
      ? new Date(note.created_on) 
      : new Date();
    setEditingNote({ 
      notes: note.notes, 
      status: note.status,
      created_on: createdOnDate
    });
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNote({ notes: '', status: 'OPEN', created_on: null });
  };

  const fetchImageUrls = async (attachmentsList: any[]) => {
    const urls: {[key: string]: string} = {};
    
    try {
      for (const attachment of attachmentsList) {
        if (attachment.files && attachment.files.length > 0) {
          for (const file of attachment.files) {
            try {
              const response = await sensorboxGroupService.getSensorboxGroupAttachment(file.filename);
              const url = URL.createObjectURL(response.data);
              urls[file.id] = url;
            } catch (error) {
              urls[file.id] = noAttachmentYet;
            }
          }
        }
      }
      setImageUrls(urls);
    } catch (error) {
      // Silently handle error
    }
  };

  const toggleEdit = () => {
    setIsEdit(!isEdit);
    if (!isEdit) {
      // When entering edit mode, ensure form data is set
      if (assetDetails) {
        setName(assetDetails.name || '');
        setLocDetails(assetDetails.loc_details || '');
        setMergeTimeout(assetDetails.group_merge_timeout?.toString() || '');
        setFormData(assetDetails);
        setExternalLinks(assetDetails.external_reference_links || []);
        setSensorboxes(assetDetails.sensorboxes || []);
        
        // Set user access
        if (assetDetails.user_access && assetDetails.user_access.length > 0 && consumerList.length > 0) {
          const userAccessArray = consumerList?.filter((item: any) =>
            assetDetails.user_access?.includes(item?._id)
          );
          const filteredUserAccess = userAccessArray?.map((item: any) => ({ value: item._id, label: item.name }));
          setUserAccess(filteredUserAccess || []);
        }
      }
    } else {
      // When canceling edit, reset to original values
      if (assetDetails) {
        setName(assetDetails.name || '');
        setLocDetails(assetDetails.loc_details || '');
        setMergeTimeout(assetDetails.group_merge_timeout?.toString() || '');
        setFormData(assetDetails);
        setExternalLinks(assetDetails.external_reference_links || []);
        setSensorboxes(assetDetails.sensorboxes || []);
        setErrors({});
      }
    }
  };

  const onChooseSensorbox = (sensorbox: any) => {
    clearFieldError('sensorboxes');
    if (searchKeyword) setSearchKeyword('');
    setSensorboxes((prev) => [...prev, sensorbox]);
  };

  const removeSensorbox = (sensorbox: any) => {
    const updated = sensorboxes.filter(s => s?._id !== sensorbox?._id);
    setSensorboxes(updated);
  };

  const convertDropdownData = (data: any, direction: string) => {
    switch (direction) {
      case 'f': return data?.map((i: any) => ({ value: i?._id, label: i?.name })) ?? [];
      case 'r': return data?.map((i: any) => ({ _id: i?.value, name: i?.label })) ?? [];
      default: return [];
    }
  };

  const handleConsumerRecipientsChange = (recipients: AlertRecipient[]) => {
    const split = splitTeamRecipients(recipients);
    setFormData((prevState: any) => ({
      ...prevState,
      consumer: split.email,
      mobile_consumer: split.sms,
      voice_consumer: split.voice,
    }));
  };

  const handleContractorRecipientsChange = (recipients: AlertRecipient[]) => {
    const split = splitTeamRecipients(recipients);
    setFormData((prevState: any) => ({
      ...prevState,
      contractor: split.email,
      mobile_contractor: split.sms,
      voice_contractor: split.voice,
    }));
  };

  const handleConsumerSelection = (option: any) => {
    clearFieldError('consumer');
    setFormData((prevState: any) => ({
      ...prevState,
      'consumer': convertDropdownData(option, 'r')
    }));
  };

  const handleMobileConsumerSelection = (option: any) => {
    clearFieldError('mobile_consumer');
    setFormData((prevState: any) => ({
      ...prevState,
      'mobile_consumer': convertDropdownData(option, 'r')
    }));
  };

  const handleContractorSelection = (option: any) => {
    clearFieldError('contractor');
    setFormData((prevState: any) => ({
      ...prevState,
      'contractor': convertDropdownData(option, 'r')
    }));
  };

  const handleMobileContractorSelection = (option: any) => {
    clearFieldError('mobile_contractor');
    setFormData((prevState: any) => ({
      ...prevState,
      'mobile_contractor': convertDropdownData(option, 'r')
    }));
  };

  const handleUserAccessSelection = (option: any) => {
    setUserAccess(option);
  };

  const clearFieldError = (field: string) => {
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const errors: any = {};
    if (!name?.trim()) {
      errors.name = constants.message.group_name_mandatory;
    }
    if (!mergeTimeout) {
      errors.group_merge_timeout = constants.message.group_merge_timeout_required;
    } else if (parseInt(mergeTimeout) < 1 || parseInt(mergeTimeout) > 60) {
      errors.group_merge_timeout = constants.message.group_merge_timeout_valid_range;
    }
    if (sensorboxes?.length < 1) {
      errors.sensorboxes = constants.message.group_sensorboxes_min;
    }
    if (locDetails && locDetails.length > 150) {
      errors.loc_details = 'Location details cannot exceed 150 characters';
    }
    setErrors(errors);
    return Object.keys(errors).some(field => field !== '');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) return;

    try {
      setProcessing(true);
      const userAccesValue = userAccess?.map((item: any) => item.value);
      
      const params = {
        name,
        loc_details: locDetails,
        sensorboxes: sensorboxes.map((s: any) => s._id) || [],
        group_merge_timeout: parseInt(mergeTimeout),
        consumer: formData?.consumer || assetDetails?.consumer || [],
        contractor: formData?.contractor || assetDetails?.contractor || [],
        mobile_consumer: formData?.mobile_consumer || assetDetails?.mobile_consumer || [],
        mobile_contractor: formData?.mobile_contractor || assetDetails?.mobile_contractor || [],
        voice_consumer: formData?.voice_consumer || assetDetails?.voice_consumer || [],
        voice_contractor: formData?.voice_contractor || assetDetails?.voice_contractor || [],
        user_access: userAccesValue || [],
        external_reference_links: externalLinks,
        attachments: attachments
      };

      await groupServices.editGroup(asset._id, params, organisationId);
      setProcessing(false);
      setIsEdit(false);
      await fetchAssetDetails();
      if (onSave) onSave();
      toast.success('Asset updated successfully');
    } catch (error: any) {
      setProcessing(false);
      toast.error(error?.response?.data?.message || 'Failed to update asset');
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  const formatDateOnly = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getStatusIcon = (isOnline: boolean) => {
    return isOnline ? (
      <i className="fa fa-circle text-success" aria-hidden="true" title="Online"></i>
    ) : (
      <i className="fa fa-circle text-danger" aria-hidden="true" title="Offline"></i>
    );
  };

  const getSignalStrengthIcon = () => {
    return (
      <i className="fa fa-signal" style={{ fontSize: '14px', color: '#28a745' }} aria-hidden="true" title="Strong Signal"></i>
    );
  };

  const customerData = (customers: any) => {
    let filteredUsers = customers?.filter((customer: any) => {
      const role = customer?.roles?.filter((role: any) => role?.role_type > 1);
      return role.length > 0;
    });
    return filteredUsers;
  };

  const demoCheck = (authStore?.currentUser?.active_role?.role_type === -1);

  // Attachments handlers
  const handleAddAttachment = () => {
    if (!authStore?.isOrgAdmin()) {
      return;
    }
    if (!asset?._id) {
      toast.error('Please save the asset first before adding attachments');
      return;
    }
    setShowAttachmentModal(true);
  };

  const handleAttachmentUpload = useCallback(async (formData: FormData) => {
    if (!authStore?.isOrgAdmin()) {
      return;
    }
    try {
      setAttachmentLoading(true);
      const addAttachment = await sensorboxGroupService.addSensorboxGroupAttachment(
        asset?._id!,
        formData,
        organisationId
      );
      if (addAttachment?.status === 200) {
        message.destroy();
        message.success(constants.message.attachmentUploadSuccess);
        await fetchAssetDetails();
        setAttachmentLoading(false);
        setShowAttachmentModal(false);
      }
    } catch (error: any) {
      message.destroy();
      message.error(error?.response?.data?.message || constants.message.internalServerError);
      setAttachmentLoading(false);
      throw error;
    }
  }, [asset?._id, organisationId]);

  const handleDeleteFile = (attachmentId: string, fileId: string) => {
    if (!authStore?.isOrgAdmin()) {
      return;
    }
    AntModal.confirm({
      title: 'Delete File',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this file? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      zIndex: 10000000,
      maskClosable: false,
      onOk: async () => {
        if (!asset?._id) return;
        try {
          setAttachmentLoading(true);
          await sensorboxGroupService.deleteSensorboxGroupAttachment(
            asset._id,
            attachmentId,
            fileId,
            organisationId
          );
          message.success("File deleted successfully");
          await fetchAssetDetails();
        } catch (error: any) {
          message.error(error?.response?.data?.message || "Failed to delete file");
        } finally {
          setAttachmentLoading(false);
        }
      }
    });
  };

  const handlePrevFile = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
    }
  };

  const handleNextFile = () => {
    const filteredAttachments = attachments.filter(att => att.type === selectedAttachmentType);
    const currentAttachment = filteredAttachments[currentAttachmentIndex];
    const currentFiles = currentAttachment?.files || [];
    if (currentFileIndex < currentFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
    }
  };

  useEffect(() => {
    setCurrentFileIndex(0);
  }, [currentAttachmentIndex]);

  useEffect(() => {
    setCurrentAttachmentIndex(0);
    setCurrentFileIndex(0);
  }, [selectedAttachmentType]);

  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        if (url !== noAttachmentYet) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imageUrls]);

  const filteredAttachments = attachments.filter(att => att.type === selectedAttachmentType);
  const currentAttachment = filteredAttachments[currentAttachmentIndex];
  const currentFiles = currentAttachment?.files || [];
  const hasAttachments = filteredAttachments && filteredAttachments.length > 0;

  // Attachment Upload Modal Component
  const AttachmentUploadModal = ({ 
    isOpen, 
    onClose, 
    onUpload, 
    loading, 
    attachmentType 
  }: {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (formData: FormData) => Promise<void>;
    loading: boolean;
    attachmentType: string;
  }) => {
    const [fileList, setFileList] = useState<any>([]);
    const [noteValue, setNoteValue] = useState('');
    const [confirmationVisible, setConfirmationVisible] = useState(false);

    const handleUpload = useCallback((info: any) => {
      const file = info?.fileList[0]?.originFileObj;
      if (file) {
        try {
          setFileList(info?.fileList);
        } catch (error) {
          // Handle error silently
        }
      }
    }, []);

    const handleOk = useCallback(async () => {
      try {
        const formData = new FormData();
        formData.append("text", noteValue || " ");
        formData.append("type", attachmentType);

        if (fileList && fileList.length > 0) {
          fileList.forEach((file: any) => {
            if (file.originFileObj) {
              formData.append("attachments", file.originFileObj);
            }
          });
        }
        
        await onUpload(formData);
        setFileList([]);
        setNoteValue('');
      } catch (error) {
        // Error handling is done in parent
      }
    }, [fileList, attachmentType, onUpload, noteValue]);

    const beforeUploadValidation = useCallback((file: any) => {
      const isDuplicate = fileList?.some((existingFile: any) => 
        existingFile?.name === file?.name && existingFile?.size === file?.size && existingFile?.type === file?.type
      );
      if (isDuplicate) {
        message.destroy();
        message.error(constants.message.imageAlreadyExist);
        return Upload.LIST_IGNORE;
      }
      
      if ((fileList?.length + 1) > 5) {
        message.destroy();
        message.error(constants.message.imageMaxLimitReached);
        return Upload.LIST_IGNORE;
      }
      
      const isImage = file?.type?.startsWith('image/');
      if (!isImage) {
        message.destroy();
        message.error(constants.message.fileTypeError);
        return Upload.LIST_IGNORE;
      }
      
      if (
        !(file.type === "image/png") &&
        !(file.type === "image/jpg") &&
        !(file.type === "image/jpeg") &&
        !(file.type === "image/webp")
      ) {
        message.destroy();
        message.error(constants.message.unsupportedImageFormat);
        return Upload.LIST_IGNORE;
      }
      
      if (file.size / 1024 / 1024 > 10) {
        message.destroy();
        message.error(constants.message.imageSizeExceeded);
        return Upload.LIST_IGNORE;
      }
      
      return false;
    }, [fileList]);

    const removeImage = useCallback((file: any) => {
      const index = fileList?.indexOf(file);
      const newFileList = fileList?.slice();
      newFileList?.splice(index, 1);
      setFileList(newFileList);
    }, [fileList]);

    const handleClose = useCallback(() => {
      setFileList([]);
      setNoteValue('');
      onClose();
    }, [onClose]);

    if (!isOpen) return null;

    return (
      <>
        <div
          className="attachment-upload-modal-root"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              handleClose();
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                borderBottom: '1px solid #e8e8e8',
                paddingBottom: '10px'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Add Attachments & Notes</h3>
              <button
                onClick={handleClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            <Spin spinning={loading}>
              <div>
                <div className="mb-3">
                  <Upload.Dragger
                    fileList={fileList}
                    showUploadList
                    onChange={handleUpload}
                    beforeUpload={beforeUploadValidation}
                    accept=".jpg,.png,.jpeg,.webp"
                    onRemove={(file) => removeImage(file)}
                    listType="picture"
                    onPreview={() => {}}
                  >
                    <p className="ant-upload-drag-icon">
                      <img
                        src={uploadImageLogo}
                        alt={"file1"}
                        style={{ width: "120px", height: "120px" }}
                      />
                    </p>
                    <p></p>
                    <p className="ant-upload-text alert-link">
                      Click or drag file to this area to upload image(s)
                    </p>
                    <p className="ant-upload-hint">
                      Maximum of 5 images allowed for a description
                    </p>
                    <p className="ant-upload-hint settings-tr">
                      Accepted formats - .jpeg .png .jpg .webp
                    </p>
                  </Upload.Dragger>
                </div>
                <br />
                <Input.TextArea
                  placeholder="Add Description..."
                  maxLength={constants.attachmentNotesLength}
                  showCount
                  onChange={(e) => setNoteValue(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  autoComplete="off"
                  value={noteValue}
                />
                
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <Button
                    onClick={handleClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => {
                      setConfirmationVisible(true);
                    }}
                    disabled={(fileList?.length === 0 && !noteValue) || loading}
                  >
                    Upload Attachment
                  </Button>
                </div>
              </div>
            </Spin>
          </div>
        </div>
        
        {confirmationVisible && (
          <div
            className="attachment-upload-modal-root"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setConfirmationVisible(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '400px',
                width: '100%',
                position: 'relative',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  borderBottom: '1px solid #e8e8e8',
                  paddingBottom: '10px'
                }}
              >
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Upload Attachments</h3>
                <button
                  onClick={() => setConfirmationVisible(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                {constants.message.uploadConfirmation}
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => setConfirmationVisible(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="attachment-upload-confirm-btn"
                  onClick={() => {
                    handleOk();
                    setConfirmationVisible(false);
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static" className="asset-details-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) 
            ? 'View Asset Details' 
            : 'Asset Details'}
          {(name || assetDetails?.name) && ` - ${name || assetDetails?.name}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <LoadingWheel />
        ) : (
          <>
            {/* Tab Navigation with Edit Button */}
            <div className="d-flex align-items-center mb-3">
              <ul className="nav nav-pills" id="pills-tab" role="tablist" style={{ flex: 1 }}>
                {/* <li className="nav-item tab-item">
                  <a
                    className={activeTab === 'details' ? "nav-link active show" : "nav-link"}
                    id="pills-details-tab"
                    data-toggle="pill"
                    href="#pills-details"
                    role="tab"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('details');
                    }}
                    aria-selected={activeTab === 'details'}
                  >
                    Asset Details
                  </a>
                </li> */}
                {/* <li className="nav-item tab-item">
                  <a
                    className={activeTab === 'attachments' ? "nav-link active show" : "nav-link"}
                    id="pills-attachments-tab"
                    data-toggle="pill"
                    href="#pills-attachments"
                    role="tab"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('attachments');
                    }}
                    aria-selected={activeTab === 'attachments'}
                  >
                    Attachments
                  </a>
                </li> */}
                
                <li className="nav-item tab-item">
                  <a
                    className={activeTab === 'maintenance' ? "nav-link active show" : "nav-link"}
                    id="pills-maintenance-tab"
                    data-toggle="pill"
                    href="#pills-maintenance"
                    role="tab"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('maintenance');
                    }}
                    aria-selected={activeTab === 'maintenance'}
                  >
                    Maintenance Notes
                  </a>
                </li>

                {canViewIssues && (
                  <li className="nav-item tab-item">
                    <a
                      className={activeTab === 'issues' ? "nav-link active show" : "nav-link"}
                      id="pills-issues-tab"
                      data-toggle="pill"
                      href="#pills-issues"
                      role="tab"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('issues');
                      }}
                      aria-selected={activeTab === 'issues'}
                    >
                      Insights / Tickets
                    </a>
                  </li>
                )}

                <li className="nav-item tab-item">
                  <a
                    className={activeTab === 'documents' ? "nav-link active show" : "nav-link"}
                    id="pills-documents-tab"
                    data-toggle="pill"
                    href="#pills-documents"
                    role="tab"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab('documents');
                    }}
                    aria-selected={activeTab === 'documents'}
                  >
                    Documents
                  </a>
                </li>
              </ul>
              {activeTab === 'details' && !authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) &&
                authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) &&
                !demoCheck && (
                <div className="edit-btn-positio ml-auto">
                  <button
                    type="button"
                    className="btn btn-edit"
                    onClick={toggleEdit}
                  >
                    {isEdit ? "Cancel Edit" : "Edit"}
                  </button>
                </div>
              )}
            </div>

            <div className="tab-content" id="pills-tabContent">
              {/* Asset Details Tab */}
              {activeTab === 'details' && (
                <div className="tab-pane fade show active" id="pills-details" role="tabpanel">
                  <form onSubmit={handleSave}>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="">Name <span className="mand-star">*</span></label>
                        <input
                          className="form-control"
                          type="text"
                          placeholder="Enter name"
                          value={name}
                          disabled={!isEdit || !authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) || authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details)}
                          onChange={(e) => {
                            clearFieldError('name');
                            setName(e.target.value);
                          }}
                          maxLength={50}
                        />
                        <div className="error-msg">{errors?.name}</div>
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="">Asset merge timeout (1-60) minute <span className="mand-star">*</span></label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter timeout (1-60) minute"
                          value={mergeTimeout}
                          disabled={!isEdit || !authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) || authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details)}
                          onChange={(e) => {
                            clearFieldError('group_merge_timeout');
                            setMergeTimeout(e.target.value);
                          }}
                          max={60}
                          min={1}
                        />
                        <div className="error-msg">{errors?.group_merge_timeout}</div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label className="custom-label" htmlFor="loc_details">
                          Location details{' '}
                          <Tooltip title="This info will be used in alert email" trigger={['hover', 'click']}>
                            <InfoCircleOutlined
                              style={{ marginLeft: 4, color: '#8c8c8c', fontSize: 13, cursor: 'help' }}
                              onClick={(e) => e.preventDefault()}
                            />
                          </Tooltip>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="loc_details"
                          name="loc_details"
                          maxLength={150}
                          placeholder="Enter location details (Max 150 characters)"
                          value={locDetails}
                          disabled={!isEdit || !authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) || authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details)}
                          onChange={(e) => {
                            clearFieldError('loc_details');
                            setLocDetails(e.target.value);
                          }}
                        />
                        <div className="error-msg">{errors?.loc_details}</div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) && (
                        <div className="col-md-6">
                          <AlertRecipientsCard
                            title="Property mgmt / service provider team"
                            recipients={demoCheck ? [] : mergeTeamRecipients(
                              formData?.contractor || assetDetails?.contractor,
                              formData?.mobile_contractor || assetDetails?.mobile_contractor,
                              formData?.voice_contractor || assetDetails?.voice_contractor
                            )}
                            availableUsers={(contractorList || []) as Array<{ _id: string; name: string }>}
                            disabled={!isEdit || !authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets)}
                            onChange={handleContractorRecipientsChange}
                          />
                        </div>
                      )}
                      <div className={authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) ? 'col-md-12' : 'col-md-6'}>
                        <AlertRecipientsCard
                          title="Consumer and team"
                          recipients={demoCheck ? [] : mergeTeamRecipients(
                            formData?.consumer || assetDetails?.consumer,
                            formData?.mobile_consumer || assetDetails?.mobile_consumer,
                            formData?.voice_consumer || assetDetails?.voice_consumer
                          )}
                          availableUsers={(consumerList || []) as Array<{ _id: string; name: string }>}
                          disabled={!isEdit || !authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) || authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details)}
                          onChange={handleConsumerRecipientsChange}
                        />
                      </div>
                    </div>

                    {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) && (
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label className="custom-label">User Access</label>
                            <Select
                              isMulti={true}
                              name="useraccess"
                              isDisabled={!isEdit || !authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets)}
                              data={customerData(consumerList)}
                              value={demoCheck ? '' : userAccess}
                              onChange={handleUserAccessSelection}
                              maxOptions={15}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sensorboxes Section */}
                    <div className="mb-3">
                      {authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) ?
                        <label htmlFor="">Sensor boxes <span className="mand-star">*</span></label>
                        :
                        <label htmlFor="">Add sensor boxes <span className="mand-star">*</span></label>
                      }
                      <div className="border rounded sensorbox-section-container">
                        <div className="row no-gutters">
                          {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) && isEdit && (
                            <div className="col-5 border-right">
                              <div className="border-bottom">
                                <input
                                  type="text"
                                  placeholder="Search"
                                  className="form-control border-0 rounded-0"
                                  value={searchKeyword || ''}
                                  disabled={!authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets)}
                                  onChange={(e) => setSearchKeyword(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.which === 13) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </div>
                              <ul className={['sensorbox-list', 'p-0'].join(' ')}>
                                {(() => {
                                  let list = sensorboxList.filter(s => (sensorboxes.findIndex(sb => sb?._id === s?._id) < 0));
                                  if (searchKeyword) {
                                    const skw = searchKeyword.toLowerCase();
                                    list = list?.filter(s => 
                                      s?.sensor_box_name?.toLowerCase().includes(skw) ||
                                      s?.alias?.toLowerCase().includes(skw) ||
                                      s?.consumer?.some((i: any) => i?.name.toLowerCase().includes(skw)) ||
                                      s?.contractor?.some((i: any) => i?.name.toLowerCase().includes(skw))
                                    );
                                  }
                                  return list.map((sensorbox: any) => (
                                    <li key={sensorbox?._id} className="sensorbox-list-item" onClick={() => onChooseSensorbox(sensorbox)}>
                                      <img className="mr-2" src={PlusBtn} style={{ height: 12 }} alt='' />
                                      <span>{sensorbox?.sensor_box_name}</span>{sensorbox?.alias && <span className='text-truncate'>&nbsp;&nbsp;( {sensorbox?.alias} )</span>}
                                    </li>
                                  ));
                                })()}
                                {(() => {
                                  let list = sensorboxList.filter(s => (sensorboxes.findIndex(sb => sb?._id === s?._id) < 0));
                                  if (searchKeyword) {
                                    const skw = searchKeyword.toLowerCase();
                                    list = list?.filter(s => 
                                      s?.sensor_box_name?.toLowerCase().includes(skw) ||
                                      s?.alias?.toLowerCase().includes(skw) ||
                                      s?.consumer?.some((i: any) => i?.name.toLowerCase().includes(skw)) ||
                                      s?.contractor?.some((i: any) => i?.name.toLowerCase().includes(skw))
                                    );
                                  }
                                  return (list.length === 0) && (
                                    <li className="text-center py-5 text-secondary">
                                      No sensor boxes
                                    </li>
                                  );
                                })()}
                              </ul>
                            </div>
                          )}
                          <div className={!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) && isEdit ? "col-7" : "col-12"}>
                            {sensorboxes?.length === 0 ? (
                              <div className="d-flex h-100 p-3">
                                <div className="m-auto text-center">
                                  <img className="mb-2" src={NoSensorBoxSelected} height={48} alt='' />
                                  <p className="m-0 text-secondary">No sensor boxes
                                    <br />{!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) && isEdit ? 'selected' : ''}</p>
                                </div>
                              </div>
                            ) : (
                              <>
                                {isEdit && !authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) ? (
                                  // Edit mode - show as tags
                                  <div className="d-flex flex-wrap p-3 " style={{minHeight:"auto"}}>
                                    {sensorboxes?.map((sensorbox: any) => {
                                      const sbName = sensorbox.sensor_box_name || sensorbox.alias || 'N/A';
                                      return (
                                        <div key={sensorbox?._id} className={['d-flex', 'align-items-center', 'bg-light', 'rounded', 'p-2', 'mb-2', 'mr-2'].join(' ')}>
                                          {sbName}
                                          <img
                                            src={Close}
                                            className="ml-2 cursor-pointer"
                                            height="10"
                                            onClick={() => removeSensorbox(sensorbox)}
                                            alt=''
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  // View mode - show as cards with status in grid
                                  <div className="row p-3">
                                    {sensorboxes?.map((sensorbox: any) => {
                                      const sbName = sensorbox.sensor_box_name || sensorbox.alias || 'N/A';
                                      const isOnline = sensorbox.is_online !== undefined ? sensorbox.is_online : true;
                                      const lastUpdated = sensorbox.last_updated_time || sensorbox.last_updated || null;
                                      
                                      return (
                                        <div key={sensorbox?._id} className="col-md-4 mb-3">
                                          <div className="card sensorbox-card">
                                            <div className="card-body">
                                              <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="card-title mb-0 text-truncate" title={sbName}>{sbName}</h6>
                                                <div className="d-flex align-items-center ml-2">
                                                  {getStatusIcon(isOnline)}
                                                  <span className="ml-2">{getSignalStrengthIcon()}</span>
                                                </div>
                                              </div>
                                              <div className="text-muted small">
                                                <div>Last Updated: {formatDate(lastUpdated)}</div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {errors?.sensorboxes && <div className="error-msg">{errors?.sensorboxes}</div>}
                    </div>

                    {/* External Reference Links Section */}
                    {assetDetails?.external_reference_links && assetDetails.external_reference_links.length > 0 && (
                      <div className="mb-3">
                        <label className="font-weight-bold">External Reference Links:</label>
                        <div className="mt-2">
                          {assetDetails.external_reference_links.map((link: any, index: number) => (
                            <div key={index} className="mb-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <span className="badge badge-secondary mr-2">{link.type || 'N/A'}</span>
                                  <strong>{link.name || 'N/A'}</strong>
                                </div>
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-link">
                                  Open Link
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isEdit && (
                      <div className="d-flex justify-content-end">
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm rounded-0 btn-dark-blue"
                          disabled={processing || demoCheck}
                        >
                          Save
                        </button>
                      </div>
                    )}
                  </form>
                </div>
              )}

              {/* Attachments Tab */}
              {activeTab === 'attachments' && (
                <div className="tab-pane fade show active" id="pills-attachments" role="tabpanel">
                  <div className="mb-4">
                    <label htmlFor="">Attachments</label>
                    <div className="border rounded">
                      <div className="row no-gutters">
                        <div className="col-12">
                          <div className="border-bottom p-3">
                            <div className="row">
                              <div className="col-md-6">
                                <label className="custom-label">Type</label>
                                <select
                                  className="form-control"
                                  value={selectedAttachmentType}
                                  disabled={!authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) || authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) || !authStore?.isOrgAdmin()}
                                  onChange={(e) => setSelectedAttachmentType(e.target.value)}
                                >
                                  <option value="model">Model</option>
                                  <option value="before_install">Before Install</option>
                                  <option value="after_install">After Install</option>
                                  <option value="miscellaneous">Miscellaneous</option>
                                </select>
                              </div>
                              <div className="col-md-6 d-flex align-items-center justify-content-end">
                                <div className="text" style={{ fontSize: '16px', fontWeight: '600' }}>
                                  Total Images: {filteredAttachments.reduce((total, attachment) => total + (attachment.files?.length || 0), 0)}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="p-3">
                            <Spin spinning={attachmentLoading}>
                              {hasAttachments ? (
                                <Card
                                  className="img-preview-card"
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div className="images-container" style={{ position: 'relative' }}>
                                    {currentFiles.length > 0 ? (
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                        {currentFiles.length > 1 && (
                                          <button
                                            onClick={handlePrevFile}
                                            disabled={currentFileIndex === 0}
                                            style={{
                                              height: '210px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              opacity: currentFileIndex === 0 ? 0.3 : 1,
                                              fontSize: '20px',
                                              background: 'transparent',
                                              border: 'none',
                                              cursor: currentFileIndex === 0 ? 'not-allowed' : 'pointer',
                                              padding: '0 8px'
                                            }}
                                          >
                                            <LeftOutlined />
                                          </button>
                                        )}
                                        <div className="single-image-container" style={{ textAlign: 'center' }}>
                                          <div
                                            className="image-item"
                                            style={{
                                              position: 'relative',
                                              display: 'inline-block',
                                              border: '1px solid #d9d9d9',
                                              borderRadius: '4px',
                                              overflow: 'hidden',
                                              cursor: 'pointer',
                                              maxWidth: '400px'
                                            }}
                                            onMouseEnter={() => setHoveredFile(currentFiles[currentFileIndex]?.id)}
                                            onMouseLeave={() => setHoveredFile(null)}
                                          >
                                            <Image
                                              src={imageUrls[currentFiles[currentFileIndex]?.id] || noAttachmentYet}
                                              alt={currentFiles[currentFileIndex]?.originalName}
                                              style={{
                                                width: '100%',
                                                height: '210px',
                                                objectFit: 'contain'
                                              }}
                                              preview={false}
                                              fallback={noAttachmentYet}
                                            />
                                            {hoveredFile === currentFiles[currentFileIndex]?.id && (
                                              <div
                                                style={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: 0,
                                                  right: 0,
                                                  bottom: 0,
                                                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '10px'
                                                }}
                                              >
                                                <Button
                                                  size="small"
                                                  type="primary"
                                                  icon={<EyeOutlined />}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPreviewVisible(true);
                                                  }}
                                                >
                                                  View
                                                </Button>
                                                {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) && (
                                                  <Button
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteFile(currentAttachment.id, currentFiles[currentFileIndex]?.id);
                                                    }}
                                                  >
                                                    Delete
                                                  </Button>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                          {currentFiles.length > 1 ? (
                                            <div style={{
                                              textAlign: 'center',
                                              marginTop: '8px',
                                              fontSize: '12px',
                                              color: '#666'
                                            }}>
                                              {currentFileIndex + 1} / {currentFiles.length}
                                            </div>
                                          ) : <div style={{ marginTop: '25px' }}></div>}
                                        </div>
                                        {currentFiles.length > 1 && (
                                          <button
                                            onClick={handleNextFile}
                                            disabled={currentFileIndex === currentFiles.length - 1}
                                            style={{
                                              height: '210px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              opacity: currentFileIndex === currentFiles.length - 1 ? 0.3 : 1,
                                              fontSize: '20px',
                                              background: 'transparent',
                                              border: 'none',
                                              cursor: currentFileIndex === currentFiles.length - 1 ? 'not-allowed' : 'pointer',
                                              padding: '0 8px'
                                            }}
                                          >
                                            <RightOutlined />
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="no-files" style={{ textAlign: 'center', padding: '20px' }}>
                                        <img
                                          src={noAttachmentYet}
                                          alt="No files"
                                          style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                                        />
                                        <p className="text-muted">No files in this attachment</p>
                                      </div>
                                    )}

                                    <div className="attachment-note" style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                      <p style={{ margin: 0, fontWeight: 'bold' }}>{currentAttachment?.text || ''}</p>
                                      <small className="text-muted">
                                        By: {currentAttachment?.creator_name || 'N/A'} • {formatDate(currentAttachment?.created_at)}
                                      </small>
                                    </div>
                                  </div>

                                  {filteredAttachments.length > 1 && (
                                    <Pagination
                                      current={currentAttachmentIndex + 1}
                                      total={filteredAttachments.length}
                                      pageSize={1}
                                      showSizeChanger={false}
                                      onChange={(page) => setCurrentAttachmentIndex(page - 1)}
                                      style={{ textAlign: "center", marginTop: "10px" }}
                                    />
                                  )}
                                </Card>
                              ) : (
                                <Card
                                  className="img-preview-card-no-attchmt"
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "200px",
                                  }}
                                >
                                  <img
                                    src={noAttachmentYet}
                                    alt="No attachments"
                                    style={{ width: "100px", height: "100px", objectFit: "contain" }}
                                  />
                                  <p className="text-muted">No Attachments Yet!</p>
                                </Card>
                              )}
                            </Spin>
                          </div>
                        </div>
                      </div>
                      
                      {authStore?.isOrgAdmin() && (
                        <div className="p-3 border-top">
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{ height: '45px', width: '100%' }}
                            disabled={!authStore?.currentUser?.access?.includes(create_or_update_sensorboxes_assets) || authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) || !authStore?.isOrgAdmin()}
                            onClick={handleAddAttachment}
                          >
                            + Add Attachment
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Maintenance Notes Tab */}
              {activeTab === 'maintenance' && (
                <div className="tab-pane fade show active" id="pills-maintenance" role="tabpanel">
                  <div className="maintenance-notes-content">
                    {maintenanceNotesLoading ? (
                      <LoadingWheel />
                    ) : (
                      <div className="table-responsive">
                        <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: '17%' }} />
                            <col style={{ width: '60%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '12%' }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>Created Date</th>
                              <th>Notes</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Add new note row */}
                            <tr>
                                <td>
                                  <DatePicker
                                    placeholderText="Select date"
                                    value={newNote.created_on}
                                    dateFormat="MM/dd/yyyy"
                                    maxDate={new Date()}
                                    isClearable={true}
                                    onChange={(value: any) => {
                                      setNewNote({ ...newNote, created_on: value });
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Enter new note..."
                                    value={newNote.notes}
                                    onChange={(e) => setNewNote({ ...newNote, notes: e.target.value })}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleCreateMaintenanceNote();
                                      }
                                    }}
                                  />
                                </td>
                                <td>
                                  <select
                                    className="form-control form-control-sm"
                                    value={newNote.status}
                                    onChange={(e) => setNewNote({ ...newNote, status: e.target.value })}
                                  >
                                    <option value="OPEN">OPEN</option>
                                    <option value="IN_PROGRESS">IN_PROGRESS</option>
                                    <option value="COMPLETED">COMPLETED</option>
                                  </select>
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={handleCreateMaintenanceNote}
                                    disabled={!newNote.notes.trim()}
                                  >
                                    Add
                                  </button>
                                </td>
                              </tr>
                            {/* Existing notes */}
                            {maintenanceNotes.map((note: any, index: number) => (
                              <tr key={note._id || index}>
                                <td>
                                  {editingNoteId === note._id ? (
                                    <DatePicker
                                      placeholderText="Select date"
                                      value={editingNote.created_on}
                                      dateFormat="MM/dd/yyyy"
                                      maxDate={new Date()}
                                      isClearable={true}
                                      onChange={(value: any) => {
                                        setEditingNote({ ...editingNote, created_on: value || new Date() });
                                      }}
                                    />
                                  ) : (
                                    formatDateOnly(note.created_on)
                                  )}
                                </td>
                                <td>
                                  {editingNoteId === note._id ? (
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={editingNote.notes}
                                      onChange={(e) => setEditingNote({ ...editingNote, notes: e.target.value })}
                                    />
                                  ) : (
                                    <span style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{note.notes}</span>
                                  )}
                                </td>
                                <td>
                                  {editingNoteId === note._id ? (
                                    <select
                                      className="form-control form-control-sm"
                                      value={editingNote.status}
                                      onChange={(e) => setEditingNote({ ...editingNote, status: e.target.value })}
                                    >
                                      <option value="OPEN">OPEN</option>
                                      <option value="IN_PROGRESS">IN_PROGRESS</option>
                                      <option value="COMPLETED">COMPLETED</option>
                                    </select>
                                  ) : (
                                    <span>{note.status}</span>
                                  )}
                                </td>
                                <td>
                                  {editingNoteId === note._id ? (
                                    <div className="d-flex" style={{ gap: '12px' }}>
                                      <button
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleUpdateMaintenanceNote(note._id)}
                                      >
                                        <SaveOutlined />
                                      </button>
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={handleCancelEdit}
                                      >
                                        <CloseOutlined />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="d-flex" style={{ gap: '12px' }}>
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleStartEdit(note)}
                                      >
                                        <EditOutlined />
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDeleteMaintenanceNote(note._id)}
                                      >
                                        <DeleteOutlined />
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {maintenanceNotes.length === 0 && (
                              <tr>
                                <td colSpan={4} className="text-center py-5">
                                  <p className="text-muted">No maintenance notes found</p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Insights / Tickets Tab */}
              {canViewIssues && activeTab === 'issues' && (
                <div className="tab-pane fade show active" id="pills-issues" role="tabpanel">
                  <div className="issue-tracker-content">
                    {issuesLoading ? (
                      <LoadingWheel />
                    ) : issues.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Ticket ID</th>
                              <th>Title</th>
                              <th>Status</th>
                              <th>Priority</th>
                              <th>Created On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {issues.map((issue: any, index: number) => {
                              const issueId = issue.issue_id || '-';
                              const handleTicketClick = () => {
                                let params = `?ticket_id=${issue._id || issue.issue_id}`;
                                if (authStore?.isSuperAdmin || (authStore?.isOrgAdmin() && authStore.currentUser.is_default)) {
                                  params += `&org_id=${organisationId || asset?.org_id || ''}`;
                                }
                                history.push({
                                  pathname: Routes.issuetrackerdetails,
                                  search: params
                                });
                              };
                              
                              return (
                                <tr key={index}>
                                  <td>
                                    {issueId !== '-' ? (
                                      <a
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleTicketClick();
                                        }}
                                        style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer' }}
                                      >
                                        {issueId}
                                      </a>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td>
                                    {issue.issue_title || '-'}
                                  </td>
                                  <td>
                                    <span>{issue.status || '-'}</span>
                                  </td>
                                  <td>{issue.issue_priority || '-'}</td>
                                  <td>{formatDate(issue.created_on || issue.timestamp)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <p className="text-muted">No issues found for this asset's sensorboxes</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && assetDetails?._id && (
                <div className="tab-pane fade show active" id="pills-documents" role="tabpanel">
                  <div className="mb-4">
                    <label className="font-weight-bold">Asset</label>
                    <p className="text-muted" style={{ fontSize: 13 }}>
                      Model, Images and Misc for the asset.
                    </p>
                    <DocumentsPanel
                      entityId={assetDetails._id}
                      api={sensorboxGroupDocumentationService}
                      disabled={!authStore?.isOrgAdmin() || demoCheck}
                      emptyModelLabel="No asset-wide model images added yet."
                    />
                  </div>

                  <div>
                    <label className="font-weight-bold">Sensorboxes</label>
                    <AssetDocumentsRollup
                      assetGroupId={assetDetails._id}
                      disabled={!authStore?.isOrgAdmin() || demoCheck}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      
      {/* Attachment Upload Modal */}
      <AttachmentUploadModal
        isOpen={showAttachmentModal}
        onClose={() => setShowAttachmentModal(false)}
        onUpload={handleAttachmentUpload}
        loading={attachmentLoading}
        attachmentType={selectedAttachmentType}
      />
      
      {/* Preview Image Modal */}
      {previewVisible && currentFiles[currentFileIndex] && (
        <Image
          src={imageUrls[currentFiles[currentFileIndex]?.id]}
          alt={currentFiles[currentFileIndex]?.originalName}
          style={{ display: 'none' }}
          preview={{
            visible: previewVisible,
            onVisibleChange: (visible) => {
              setPreviewVisible(visible);
            }
          }}
        />
      )}
    </Modal>
  );
};

export default AssetDetailsModal;